/**
 * BalanceScale — hand-authored SVG diagram used by the diagrammatic
 * infographic sample. Vector-only, resolution-independent, no raster.
 * Labels are rendered as HTML text OUTSIDE the SVG so Indic scripts
 * fall through to the app's normal font stack (see the Indic CSS
 * override notes in BookInfographicPage.css) — no font-embedding risk.
 *
 * Props: leftLabel, leftValue, rightLabel, rightValue, tilt ("left" | "right" | "level")
 */
import React from "react";

const GOLD = "#C9A84C";
const DARK = "#4A2A15";
const CREAM = "#FBF2DF";

export default function BalanceScale({
  leftLabel = "20% Effort",
  leftValue = "20%",
  rightLabel = "80% Results",
  rightValue = "80%",
  tilt = "left",  // which side is lower (heavier)
}) {
  // Tilt geometry: the beam rotates ±8deg around the fulcrum.
  const rot = tilt === "left" ? 8 : tilt === "right" ? -8 : 0;

  return (
    <div
      className="bic-diagram-balance"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
        padding: "8px 0 4px",
      }}
    >
      <svg
        viewBox="0 0 320 260"
        width="280"
        height="230"
        aria-hidden="true"
        style={{ overflow: "visible" }}
      >
        {/* Fulcrum column */}
        <rect x="152" y="90" width="16" height="130" fill={DARK} rx="2" />
        <ellipse cx="160" cy="220" rx="70" ry="6" fill={DARK} opacity="0.55" />
        {/* Base plate */}
        <rect x="90" y="222" width="140" height="10" fill={DARK} rx="2" />
        {/* Beam group — rotates around fulcrum (160,90) */}
        <g transform={`rotate(${rot} 160 90)`}>
          {/* Beam */}
          <rect x="35" y="86" width="250" height="8" fill={DARK} rx="2" />
          {/* Left pan chain */}
          <line x1="60" y1="90" x2="60" y2="140" stroke={DARK} strokeWidth="2" />
          {/* Left pan */}
          <path
            d="M 20 140 Q 60 165 100 140 Z"
            fill={GOLD}
            stroke={DARK}
            strokeWidth="1.5"
            opacity="0.9"
          />
          {/* Left pan value text (SVG text — English/Latin OK, Indic falls
              back but we mainly place the numeric value here anyway) */}
          <text
            x="60"
            y="132"
            textAnchor="middle"
            fill={DARK}
            style={{ fontFamily: "Cinzel, serif", fontSize: 14, fontWeight: 600 }}
          >
            {leftValue}
          </text>

          {/* Right pan chain */}
          <line x1="260" y1="90" x2="260" y2="140" stroke={DARK} strokeWidth="2" />
          {/* Right pan */}
          <path
            d="M 220 140 Q 260 165 300 140 Z"
            fill={GOLD}
            stroke={DARK}
            strokeWidth="1.5"
          />
          <text
            x="260"
            y="132"
            textAnchor="middle"
            fill={DARK}
            style={{ fontFamily: "Cinzel, serif", fontSize: 14, fontWeight: 600 }}
          >
            {rightValue}
          </text>

          {/* Decorative sparkle on the heavier side */}
          {tilt !== "level" && (
            <g transform={tilt === "left" ? "translate(60,158)" : "translate(260,158)"}>
              <circle cx="0" cy="0" r="3" fill={GOLD} />
              <circle cx="-8" cy="4" r="2" fill={GOLD} opacity="0.8" />
              <circle cx="10" cy="6" r="2.4" fill={GOLD} opacity="0.7" />
            </g>
          )}
        </g>
        {/* Fulcrum pivot cap */}
        <polygon
          points="160,72 148,92 172,92"
          fill={DARK}
        />
      </svg>
      {/* HTML labels — rendered outside SVG so Indic script fonts kick in */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: 300,
          fontFamily: "'EB Garamond', serif",
          fontSize: 13,
          fontStyle: "italic",
          color: DARK,
        }}
      >
        <span style={{ textAlign: "center", flex: 1 }}>{leftLabel}</span>
        <span style={{ textAlign: "center", flex: 1 }}>{rightLabel}</span>
      </div>
    </div>
  );
}
